import React, { memo, useCallback, useMemo } from 'react';

import type { SelectDataItem } from '../../../../../Shared/js/@types/SelectDataItem';
import Select from '../../../../../Shared/React/js/components/Select';
import { ScreenSize } from '../../../../../Shared/React/js/utils/Hooks';

export interface SpeedRange {
    min: number;
    max: number;
    label: string;
}

export interface Props {
    dataWidth?: string;
    screenSize?: ScreenSize;
    primaryFilterAsSpeed: boolean;
    isModal?: boolean;
    className?: string;
    prefixSpeedRangeWithSpeed?: boolean;

    speedDescription?: string;
    speedFormat?: string;
    speed?: number;
    maxSpeed?: number;
    speedValues?: SelectDataItem[];
    speedRanges?: SpeedRange[];
    onSpeedChange?: (value: number | { min: number; max: number }) => void;

    dataDescription?: string;
    dataFormat?: string;
    data?: number;
    dataValues?: SelectDataItem[];
    onDataChange?: (value: number) => void;
}

// Pure function: extracted to module level, allocated once
const findCurrentRangeIndex = (speed: number, maxSpeed: number | undefined, ranges: SpeedRange[]): number => {
    if (!ranges || ranges.length === 0) {
        return -1;
    }
    if (speed === 0) {
        if (maxSpeed === 0 || maxSpeed === undefined) {
            const anySpeedRangeIndex = ranges.findIndex(r => r.min === 0 && r.max === 0);
            return anySpeedRangeIndex >= 0 ? anySpeedRangeIndex : -1;
        }
        const matchingRangeIndex = ranges.findIndex(r => r.min === 0 && r.max === maxSpeed);
        if (matchingRangeIndex >= 0) {
            return matchingRangeIndex;
        }
    }

    if (maxSpeed !== undefined && maxSpeed > 0) {
        const exactMatchIndex = ranges.findIndex(r => r.min === speed && r.max === maxSpeed);
        if (exactMatchIndex >= 0) {
            return exactMatchIndex;
        }
    }

    for (let i = 0; i < ranges.length; i++) {
        const range = ranges[i];
        const maxValid = range.max < 0; // unbounded
        const speedInRange = speed >= range.min && (maxValid || speed <= range.max);
        if (speedInRange) {
            if (maxSpeed !== undefined && maxSpeed > 0) {
                if (maxSpeed === range.max) {
                    return i;
                }
                continue;
            }
            return i;
        }
    }

    const lastRange = ranges[ranges.length - 1];

    if (lastRange.max >= 0 && speed > lastRange.max) {
        return ranges.length - 1;
    }

    return -1;
};

const SpeedDataSelect = (props: Props) => {
    const {
        dataWidth = props.dataWidth || (props.screenSize === ScreenSize.Mobile ? '100%' : 'fit'),
        isModal = false,
        primaryFilterAsSpeed,
        speedDescription,
        speedFormat,
        prefixSpeedRangeWithSpeed = false,
        speed,
        maxSpeed,
        speedValues,
        speedRanges,
        onSpeedChange,
        dataDescription,
        data,
        dataValues,
        onDataChange,
        className
    } = props;

    // Memoize handlers to prevent child re-render on parent re-render
    const handleRangeChange = useCallback(
        (selectedValue: string | number | string[] | number[]) => {
            const selectedIndex = Array.isArray(selectedValue)
                ? parseInt(selectedValue[0] as string, 10)
                : parseInt(selectedValue as string, 10);
            if (!isNaN(selectedIndex) && selectedIndex >= 0 && selectedIndex < (speedRanges?.length ?? 0)) {
                const selectedRange = speedRanges![selectedIndex];
                onSpeedChange?.({ min: selectedRange.min, max: selectedRange.max });
            }
        },
        [speedRanges, onSpeedChange]
    );

    const handleSpeedChange = useCallback(
        (value: string | number | string[] | number[]) => {
            onSpeedChange?.(value as number);
        },
        [onSpeedChange]
    );

    const handleDataChange = useCallback(
        (value: string | number | string[] | number[]) => {
            onDataChange?.(value as number);
        },
        [onDataChange]
    );

    const speedRangeItems = useMemo(() => {
        const speedPrefix = (speedFormat || '').replace(/\{\d+[^}]*\}/g, '').trim();
        return speedRanges
            ? speedRanges.map((range, index) => {
                  const isAnySpeedRange = range.min === 0 && range.max === 0;
                  const hasPrefix = speedPrefix && range.label.toLowerCase().startsWith(speedPrefix.toLowerCase());
                  const selectedLabel =
                      !prefixSpeedRangeWithSpeed || isAnySpeedRange || !speedPrefix || hasPrefix
                          ? range.label
                          : `${speedPrefix} ${range.label}`;

                  return {
                      key: index,
                      value: range.label,
                      title: selectedLabel
                  };
              })
            : [];
    }, [speedRanges, speedFormat, prefixSpeedRangeWithSpeed]);

    const currentSpeedIndex = useMemo(() => {
        return speedRanges && speedRanges.length > 0 ? findCurrentRangeIndex(speed || 0, maxSpeed, speedRanges) : -1;
    }, [speed, maxSpeed, speedRanges]);

    const speedValueItems = useMemo(() => speedValues || [], [speedValues]);
    const dataValueItems = useMemo(() => dataValues || [], [dataValues]);
    const dataAttributes = useMemo(() => ({ 'data-dropup-auto': 'false' }), []);

    // Build render props based on primaryFilterAsSpeed flag
    let type: string;
    let description: string;
    let selectDataItems: SelectDataItem[] = [];
    let selectedValue: number = 0;
    let onValueChanged: (value: string | number | string[] | number[]) => void;

    if (primaryFilterAsSpeed) {
        type = 'speed';
        description = speedDescription || '';

        if (speedRanges && speedRanges.length > 0) {
            selectDataItems = speedRangeItems;
            selectedValue = currentSpeedIndex >= 0 ? currentSpeedIndex : 0;
            onValueChanged = handleRangeChange;
        } else {
            selectDataItems = speedValueItems;
            selectedValue = speed || 0;
            onValueChanged = handleSpeedChange;
        }
    } else {
        type = 'data';
        description = dataDescription || '';
        selectDataItems = dataValueItems;
        selectedValue = data || 0;
        onValueChanged = handleDataChange;
    }

    const selectComponent = (
        <Select
            key={`${type}Select`}
            selectClassName={className}
            optionGroups={[{ label: description, values: selectDataItems }]}
            onChange={onValueChanged}
            selectedValues={selectedValue}
            dataWidth={dataWidth}
            dataAttributes={dataAttributes}
        />
    );
    return isModal ? <div className="selectpicker-wide">{selectComponent}</div> : selectComponent;
};

export default memo(SpeedDataSelect);
